import { useEffect, useState } from 'react';
import { fg } from '@atlassian/jira-feature-gating';

type CancelIdleCallback = () => void;

/**
 * Version of requestIdleCallback with fallback to setTimeout of 200ms.
 */
const scheduleIdle = (cb: () => void): CancelIdleCallback => {
	if (typeof requestIdleCallback === 'undefined') {
		const timer = setTimeout(cb, 200);
		return () => {
			clearTimeout(timer);
		};
	}

	const idleCallback = requestIdleCallback(cb);
	return () => {
		cancelIdleCallback(idleCallback);
	};
};

type UseRenderFullItem = {
	isVisible: boolean;
	isScrolling: boolean;
};

/**
 * Return a boolean indicating whether to render full or lite items on a virtual list.
 *
 * The logic is:
 *
 * * While scrolling, don't update any lite cards into full cards
 * * When stopped, update the visible cards immediately
 * * When stopped, schedule idle updates for cards outside of the viewport
 *   (those that are overflowing as the v-list buffer)
 *
 * @param isVisible
 * @param isScrolling
 * @returns {boolean}
 */
export const useRenderFullItem = ({ isVisible, isScrolling }: UseRenderFullItem): boolean => {
	const [renderFullCard, setRenderFullCard] = useState(
		fg('jira-concurrent-backlog-perf') ? isVisible && !isScrolling : false,
	);

	useEffect(() => {
		if (isScrolling) {
			return undefined;
		}

		if (isVisible) {
			setRenderFullCard(true);
			return undefined;
		}

		return scheduleIdle(() => {
			setRenderFullCard(true);
		});
	}, [isScrolling, isVisible]);

	return renderFullCard;
};
